import React, { useState, useEffect } from 'react';

// MUI
import { Box, InputLabel, TextField, Typography } from '@mui/material';

import OLCBaseModal from '../../BaseModal';
import DatePicker from '~/components/General/OlcDatePicker';
import { dateProtector } from '~/utils/date-format';
import './styles.scss';

const UpdatePricingModal = ({ open, onClose, onSubmit, errors = null }) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const [loader, setLoader] = useState(false);
  const [values, setValues] = useState({
    effectiveDate: null,
    notes: '',
  });

  // Reset form when modal opens or closes
  useEffect(() => {
    if (!open) {
      setValues({
        effectiveDate: null,
        notes: '',
      });
    }
  }, [open]);

  // Format error messages from the errors object
  const formatErrorMessages = () => {
    if (!errors) return [];

    const errorMessages = [];

    // Extract error messages from the errors object
    if (typeof errors === 'object') {
      Object.values(errors).forEach((errorArray) => {
        if (Array.isArray(errorArray)) {
          errorArray.forEach((error) => {
            if (typeof error === 'string') {
              errorMessages.push(error);
            }
          });
        } else if (typeof errorArray === 'string') {
          errorMessages.push(errorArray);
        }
      });
    } else if (typeof errors === 'string') {
      errorMessages.push(errors);
    }

    return errorMessages;
  };

  const errorMessages = formatErrorMessages();

  const handleSave = async () => {
    // Validate that effective date is selected
    if (!values.effectiveDate) {
      return;
    }
    setLoader(true);
    await onSubmit?.(values);
    setTimeout(() => {
      setLoader(false);
    }, 1000);
  };

  // Disable save button if effective date is not selected
  const isSaveDisabled = !values.effectiveDate;

  return (
    <OLCBaseModal
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title="Update Pricing"
      subTitle="You're about to update the tier pricing for this account. Once saved, these changes will take effect on the Effective Date you've selected."
      primaryText="Save"
      width={640}
      loading={loader}
      disableScrollLock={false}
      paperClassName="olcAdminFormModal pricingUpdateModalWrapper"
      disablePrimary={isSaveDisabled}
    >
      <Box className="pricingUpdateContent">
        <Box className="olcAdminField">
          <InputLabel className="fieldLabel">Effective Date*</InputLabel>
          <Box className="mailedDate newDatePicker updatedDesign">
            <Box className="selectDate">
              <DatePicker
                selected={values.effectiveDate}
                onChange={(date) => {
                  if (date) {
                    dateProtector(date);
                  }
                  setValues((p) => ({ ...p, effectiveDate: date }));
                }}
                dateFormat="MM/dd/yyyy"
                className="datePicker"
                placeholderText="Select date"
                minDate={tomorrow}
              />
            </Box>
          </Box>
        </Box>

        <Box className="olcAdminField">
          <InputLabel className="fieldLabel">Notes</InputLabel>
          <TextField
            className="olcAdminTextField notesInput"
            multiline
            minRows={4}
            placeholder="Add any notes..."
            value={values.notes.trimStart()}
            onChange={(e) => setValues((p) => ({ ...p, notes: e.target.value }))}
          />
        </Box>
      </Box>

      {errorMessages.length > 0 && (
        <Box className="row columnRow" sx={{ margin: '5px', marginBottom: '30px', overflowX: 'hidden' }}>
          <InputLabel className="fieldLabel" sx={{ marginTop: '15px' }}>
            Validation Errors:
          </InputLabel>
          <Box sx={{ maxHeight: '200px', overflow: 'scroll', overflowX: 'hidden' }}>
            {errorMessages.map((error, index) => (
              <Typography key={index} variant="body2" sx={{ marginBottom: '4px', color: 'red' }}>
                • {error}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </OLCBaseModal>
  );
};

export default UpdatePricingModal;
